/**
 * Berechnungs-Engine — vollständig konfigurationsgetrieben (KONZEPT §5, §7.3).
 *
 * Kernmodell (Stand 2026-09, Userentscheid):
 *  - Die Bänder sind immer dieselben — Überstunden gibt es **nur** über der
 *    Überstundenschwelle, alles darunter ist Mehrarbeit:
 *      vereinbarte Wochenstunden … 38,5 h  → Teilzeit-Mehrarbeit (Zuschlag 25 %)
 *      38,5 h … 40 h                        → Mehrarbeit          (Zuschlag 50 %)
 *      ab 40 h                              → Überstunden, fertig gerechnet mit
 *                                             (h × 167/143) × (1 + Satz)
 *  - **Wann** abgerechnet wird, steuert `ueberstundenZeitraum`:
 *      'woechentlich' — Mehrzeit entsteht erst über den vereinbarten Wochenstunden;
 *                       die Überstunden sind die zuletzt geleisteten Stunden der
 *                       Woche, darunter liegt die Mehrarbeit.
 *      'taeglich'     — jede Stunde über dem **Tages-Soll** ist sofort Mehrzeit
 *                       (9 statt 8 geplante Stunden ⇒ 1 h sofort). Aufgeteilt wird
 *                       nach der Mehrzeit der Woche **in ihrer Reihenfolge**: Die
 *                       ersten Stunden füllen die Mehrarbeit (Vollzeit 40 − 38,5 =
 *                       1,5 h), jede weitere ist Überstunde — unabhängig von der
 *                       Wochensumme (AP-22 Teil 5).
 *      'monatlich' /
 *      'monate'       — nur der Saldo am Periodenende; die Wochenschwellen werden
 *                       auf die Periode hochgerechnet.
 *  - Die **Woche ist die Kalenderwoche**, auch über die Monatsgrenze (AP-22 Teil 5):
 *    Die Schwellen werden am Monatsrand nicht gekürzt. Die Tage des Vormonats ab dem
 *    Montag (`kontext.vorwoche`) zählen mit; was sie an Mehrarbeit und Überstunden
 *    verbraucht haben, steht dem Monat nicht mehr zu. In die Wochenschwelle zählen
 *    Arbeit und bezahlte Gutschrift (Feiertag, Urlaub, Krankenstand) — nicht
 *    Zeitausgleich und nicht der persönliche Termin.
 *  - Stunden **außerhalb der Betriebszeit** sowie **Feiertags-/Sonntagsstunden**
 *    werden ebenfalls gewichtet gerechnet ((h × 167/143) × (1 + Satz)).
 *  - **Wegzeit** (Reise) außerhalb der Betriebszeit: beim **Lenker** Überstunde,
 *    sonst normale Arbeitsstunde ohne Zuschlag (KV Metallgewerbe VIII/7). Die
 *    Lenker-Wegzeit ist **Teil** der Mehrzeit, nicht zusätzlich dazu (Auftraggeber
 *    30.9.2026): Sie wird zuerst Überstunde, die übrige Mehrzeit wird wie sonst
 *    verteilt, und Mehrarbeit + Überstunden bleiben NAZ − Soll. Liegt keine
 *    Mehrzeit an, bleibt sie Arbeitszeit mit dem Zuschlag außerhalb der Betriebszeit.
 *  - **Zeitausgleich**: Saldo = geleistete Zeit − Soll − konsumierter ZA, je Monat
 *    für sich (kein Übertrag). Fehlstunden eines Tages werden am Tag ausgewiesen.
 *  - Abwesenheiten mit „als Soll" (Urlaub/Krank/…) zählen mit dem Tages-Soll als
 *    Normalstunden; gesetzliche Feiertage werden automatisch gutgeschrieben.
 *  - **Stundenrahmen** (AP-22 Teil 7, `kontext.rahmen`): x Stunden je Woche oder Monat, kein
 *    Tages-Soll. Mit Mehrzeit verteilt die Woche wie der Wochenmodus, der Monat als Saldo; sonst
 *    nur Soll, Ist und Differenz je Zeitraum (`summen.rahmen`).
 *  - **Persönliche Termine** liegen außerhalb von Kommt–Geht: Sie zählen als erfüllte
 *    Zeit (NAZ, Normalstunden) und gehen vom Zeitausgleich ab. Mehrzeit berühren sie nicht.
 *
 * Jede Minute liegt in genau **einem** Eimer (normal / Mehrarbeit / Überstunde)
 * und trägt höchstens **einen** Zuschlagssatz (den höchsten zutreffenden) — es gibt
 * also keine Doppelzählung zwischen den ausgewiesenen Größen.
 */
import { wocheFuerDatum } from './zyklus'
import {
  dauerStunden,
  hhmmZuMinuten,
  montagKey,
  rundeStunden,
  tageImMonat,
  wochentagVon,
} from './zeit'
import type {
  AbwesenheitId,
  AbwesenheitInfo,
  TagAbwesenheit,
  Betriebszeit,
  EngineKonfig,
  EntfernungStufe,
  MitarbeiterKontext,
  MonatsErgebnis,
  PeriodeVorher,
  RahmenPeriode,
  TagEingabe,
  TagErgebnis,
  ZuschlagAnteil,
  TagZulage,
} from './types'

const EPS = 1e-9

/** true, wenn `clockMin` im Fenster [von,bis) liegt (Wrap über Mitternacht erlaubt). */
function imFensterMin(clockMin: number, von: number, bis: number): boolean {
  if (von === bis) return false
  return von < bis ? clockMin >= von && clockMin < bis : clockMin >= von || clockMin < bis
}

/** In welchem Eimer eine Arbeitsminute liegt (genau einer je Minute). */
type Eimer = 'normal' | 'mehr_tz' | 'mehr_vz' | 'ue'

/** Eine getaktete Arbeitsminute des Tages. */
interface Minute {
  /** Uhrzeit (Minuten ab Mitternacht) oder null, wenn nur eine Stundensumme erfasst ist. */
  clock: number | null
  /** Gehört zur Wegzeit (Reise). */
  weg: boolean
  /** Liegt außerhalb der Betriebszeit dieses Wochentags. */
  ausserhalb: boolean
  eimer: Eimer
}

/** Zwischenstand eines Tages, bevor die Wochenverteilung greift. */
interface TagIntern {
  e: TagErgebnis
  minuten: Minute[]
  /** Tatsächlich geleistete Arbeitszeit (= NAZ) in Stunden. */
  arbeit: number
  /** Zu leistendes Soll nach Abzug von Gutschriften (Urlaub/Feiertag). */
  arbeitSoll: number
  /** Als Soll gutgeschriebene Stunden (Abwesenheit/Feiertag). */
  sollGutschrift: number
  /** Davon für die Wochenschwelle wie Arbeit zu zählen — alles außer Zeitausgleich (Teil 5). */
  gutschriftZaehlt: number
  lenker: boolean
  istSonntag: boolean
  /** Rückruf am Tag (KV XIV/8.11): Die Überstunden tragen mindestens den Rückruf-Satz. */
  rueckruf: boolean
  /** 24./31.12.: Uhrzeit (Minuten), ab der gearbeitete Zeit den Halbtag-Satz trägt — sonst `null`. */
  halbtagAb: number | null
  /** Arbeitsminuten der Kalenderwoche **vor** diesem Tag, auch aus dem Vormonat (KV XIV/8.5). */
  wocheVorherMin: number
  /** Die Eingabe des Tages — für die Zulagen (erfasste Stunden, Kennzeichen, Nächtigung, km). */
  eingabe: TagEingabe
}

/** Schlüssel des gesetzlichen Feiertags in `zaehler.nlz`. */
export const NLZ_FEIERTAG = 'Feiertag'
/** Schlüssel der am 24./31.12. fortgezahlten Stunden in `zaehler.nlz` (AP-22 Teil 6). */
export const NLZ_HALBTAG = '24./31.12.'

function zpad(n: number): string {
  return String(n).padStart(2, '0')
}

function folgetag(iso: string): string {
  const d = new Date(`${iso}T00:00:00.000Z`)
  d.setUTCDate(d.getUTCDate() + 1)
  return d.toISOString().slice(0, 10)
}

/** Baut eine Nachschlagefunktion Tages-Soll(datum) aus dem Zyklus-Sollzeitplan. */
function sollLookup(kontext: MitarbeiterKontext): (datum: string) => number {
  const map = new Map<string, number>()
  for (const s of kontext.sollzeit) map.set(`${s.woche}-${s.wochentag}`, s.sollStunden)
  const zyklus = Math.max(1, kontext.zyklusWochen)
  return (datum) => {
    const woche = wocheFuerDatum(datum, zyklus, kontext.ankerDatum)
    return map.get(`${woche}-${wochentagVon(datum)}`) ?? 0
  }
}

/** Summe des Wochen-Solls der Zyklus-Woche, in die `datum` fällt. */
function wochenSollLookup(kontext: MitarbeiterKontext): (datum: string) => number {
  const proWoche = new Map<number, number>()
  for (const s of kontext.sollzeit) {
    proWoche.set(s.woche, (proWoche.get(s.woche) ?? 0) + s.sollStunden)
  }
  const zyklus = Math.max(1, kontext.zyklusWochen)
  return (datum) => proWoche.get(wocheFuerDatum(datum, zyklus, kontext.ankerDatum)) ?? 0
}

/**
 * Durchschnittliches Wochen-Soll über alle Zyklus-Wochen. Damit lässt sich das
 * Soll einer Periode in „volle Wochen" umrechnen, auch bei mehrwöchigen Zyklen.
 */
function wochenSollSchnitt(kontext: MitarbeiterKontext): number {
  const summe = kontext.sollzeit.reduce((a, s) => a + s.sollStunden, 0)
  return summe / Math.max(1, kontext.zyklusWochen)
}

/** Ist der Mitarbeiter Teilzeit? Ohne Stammdatenangabe aus den Wochenstunden abgeleitet. */
function istTeilzeit(kontext: MitarbeiterKontext, konfig: EngineKonfig): boolean {
  if (kontext.beschaeftigung != null) return kontext.beschaeftigung === 'teilzeit'
  return (kontext.wochenstunden ?? konfig.mehrarbeitSchwelle) < konfig.mehrarbeitSchwelle - EPS
}

/**
 * Reise-Intervalle des Tages auf der Uhr (Minuten; Ende darf 1440 überschreiten).
 *
 * AP-22 Teil 7 (Auftraggeber 28.10.2026): Die Reisezeit teilt sich **immer 50/50** auf Hin- und
 * Rückfahrt — 3 h sind 1,5 h hin und 1,5 h zurück. Der Hinweg beginnt bei der Abfahrt (sonst
 * Kommt), der Rückweg bei der Rückfahrt; ist keine erfasst, endet er bei Geht. Nur mit
 * Nächtigung gibt es am selben Tag eine Fahrt allein: am **Anreisetag** keinen Rückweg, am
 * **Rückreisetag** keinen Hinweg — dort liegt die ganze Reisezeit auf der einen Fahrt.
 *
 * Bis 0.11 lag die ganze Reisezeit ab der Abfahrt, wenn keine Rückfahrt erfasst war: Der
 * Rückweg am Abend fehlte, und Wegzeit nach dem Ende der Betriebszeit wurde nie erkannt.
 */
function reiseIntervalle(eingabe: TagEingabe, vortagNaechtigung: boolean): [number, number][] {
  const reiseMin = Math.max(0, eingabe.reisezeitStd ?? 0) * 60
  if (reiseMin <= 0) return []
  const ab = hhmmZuMinuten(eingabe.abfahrt ?? null)
  const rf = hhmmZuMinuten(eingabe.rueckfahrt ?? null)
  const kommt = hhmmZuMinuten(eingabe.kommt ?? null)
  const geht = hhmmZuMinuten(eingabe.geht ?? null)
  const heute = eingabe.naechtigung === true
  const fahrt = (beginn: number | null, dauer: number): [number, number][] =>
    beginn === null ? [] : [[beginn, beginn + dauer]]

  // Anreisetag: nur hin. Rückreisetag: nur zurück — die Abfahrt ist dort die Abfahrt nach Hause.
  if (heute && !vortagNaechtigung) return fahrt(ab ?? kommt, reiseMin)
  if (vortagNaechtigung && !heute) return fahrt(rf ?? ab ?? (geht === null ? null : geht - reiseMin), reiseMin)
  // Zwischentag (gestern und heute Nächtigung): wie erfasst.
  if (heute && vortagNaechtigung) {
    if (ab !== null && rf !== null) return [...fahrt(ab, reiseMin / 2), ...fahrt(rf, reiseMin / 2)]
    return fahrt(ab ?? rf, reiseMin)
  }
  const halb = reiseMin / 2
  return [...fahrt(ab ?? kommt, halb), ...fahrt(rf ?? (geht === null ? null : geht - halb), halb)]
}

function inIntervallen(clock: number, intervalle: [number, number][]): boolean {
  for (const [s, e] of intervalle) {
    if ((clock >= s && clock < e) || (clock + 1440 >= s && clock + 1440 < e)) return true
  }
  return false
}

/**
 * Baut die Minutenliste eines Arbeitstages: genau `naz × 60` Minuten. Mit Kommt/Geht
 * sind es echte Uhrzeiten (die Pause wird aus der Mitte des Blocks herausgenommen),
 * sonst ein synthetischer Block ohne Uhrzeit (dann greifen Zeitfenster nicht).
 */
function baueMinuten(
  eingabe: TagEingabe,
  naz: number,
  pauseStd: number,
  bz: Betriebszeit | null,
  vortagNaechtigung: boolean
): Minute[] {
  const ziel = Math.round(naz * 60)
  if (ziel <= 0) return []
  const k = hhmmZuMinuten(eingabe.kommt ?? null)
  const g = hhmmZuMinuten(eingabe.geht ?? null)
  const clocks: (number | null)[] = []
  if (k !== null && g !== null && g > k) {
    for (let m = k; m < g; m++) clocks.push(m)
    const pauseMin = Math.round(pauseStd * 60)
    if (pauseMin > 0 && clocks.length > pauseMin) {
      clocks.splice(Math.floor((clocks.length - pauseMin) / 2), pauseMin)
    }
  }
  while (clocks.length > ziel) clocks.pop()
  while (clocks.length < ziel) clocks.push(null)

  const weg = reiseIntervalle(eingabe, vortagNaechtigung)
  const bzVon = bz ? (hhmmZuMinuten(bz.von) ?? 0) : 0
  const bzBis = bz ? (hhmmZuMinuten(bz.bis) ?? 0) : 0
  return clocks.map((clock) => ({
    clock,
    weg: clock !== null && inIntervallen(clock, weg),
    // Ohne Betriebszeit (z. B. Samstag/Sonntag) liegt der ganze Tag außerhalb.
    // Ohne Uhrzeiten lässt sich das nicht bestimmen → als innerhalb gewertet.
    ausserhalb: bz === null ? true : clock !== null && !imFensterMin(clock, bzVon, bzBis),
    eimer: 'normal' as Eimer,
  }))
}

const STUFEN_RANG: Record<EntfernungStufe, number> = { klein: 1, mittel: 2, gross: 3 }

function hoehereStufe(
  a: EntfernungStufe | null,
  b: EntfernungStufe | null
): EntfernungStufe | null {
  if (!a) return b
  if (!b) return a
  return STUFEN_RANG[a] >= STUFEN_RANG[b] ? a : b
}

/**
 * Entfernungszulage (KV Metallgewerbe VIII/2-4):
 *  - Grundfall nach Dauer (Montagezeit + Reisezeit): > 6 h → klein, > 11 h → mittel.
 *    Mit `entfernungKleinMitPause` bemisst sich die kleine Zulage an der ununterbrochenen
 *    Abwesenheit `spanne` — die Pause zählt mit (VIII/2); für die 11 Stunden bleibt sie
 *    abgezogen (VIII/3: „ohne Mittagspause").
 *  - Reise **mit Nächtigung**: Zwischentag → groß; Anreisetag → Abfahrt vor 11:00
 *    groß, sonst mittel; Rückreisetag → Ankunft im Betrieb nach 17:00 groß, sonst
 *    mittel. Es gilt jeweils die höhere der zutreffenden Stufen.
 */
function ermittleEntfernung(
  eingabe: TagEingabe,
  montagezeit: number,
  konfig: EngineKonfig,
  vortagNaechtigung: boolean,
  /** Abfahrt (bzw. Kommt) bis Rückfahrt (bzw. Geht) in Stunden — samt Pause. */
  spanne = 0
): EntfernungStufe | null {
  const reise = Math.max(0, eingabe.reisezeitStd ?? 0)
  const dauer = montagezeit + reise
  const dauerKlein = konfig.entfernungKleinMitPause ? Math.max(dauer, spanne) : dauer
  let basis: EntfernungStufe | null = null
  if (dauer > konfig.entfernungSchwelleMittel + EPS) basis = 'mittel'
  else if (dauerKlein > konfig.entfernungSchwelleKlein + EPS) basis = 'klein'

  const naechtigung = eingabe.naechtigung === true
  if (naechtigung && vortagNaechtigung) return 'gross' // ganzer Tag auswärts
  if (naechtigung) {
    const ab = hhmmZuMinuten(eingabe.abfahrt ?? null) ?? hhmmZuMinuten(eingabe.kommt ?? null)
    const grenze = hhmmZuMinuten(konfig.entfernungAnreiseGrenze) ?? 11 * 60
    return hoehereStufe(basis, ab !== null && ab < grenze ? 'gross' : 'mittel')
  }
  if (vortagNaechtigung) {
    const start = hhmmZuMinuten(eingabe.rueckfahrt ?? null) ?? hhmmZuMinuten(eingabe.geht ?? null)
    const beide = eingabe.abfahrt != null && eingabe.rueckfahrt != null
    const fahrt = (beide ? reise / 2 : reise) * 60
    const grenze = hhmmZuMinuten(konfig.entfernungRueckreiseGrenze) ?? 17 * 60
    const ankunft = start !== null ? start + fahrt : null
    return hoehereStufe(basis, ankunft !== null && ankunft > grenze ? 'gross' : 'mittel')
  }
  return basis
}

function entfernungBetrag(stufe: EntfernungStufe | null, konfig: EngineKonfig, verpflegungGestellt = false): number {
  if (stufe === 'klein') return konfig.entfernungKlein
  if (stufe === 'mittel') return konfig.entfernungMittel
  if (stufe === 'gross') {
    // Verpflegung beigestellt: nur ein Teil der großen Zulage (KV VIII/4).
    return verpflegungGestellt
      ? Math.round(konfig.entfernungGross * konfig.entfernungVerpflegungProzent) / 100
      : konfig.entfernungGross
  }
  return 0
}

/**
 * Tagesbasis: alles, was ohne Kenntnis der Woche feststeht (Zeiten, Pause, Soll-
 * Gutschriften, Montagezeit, Entfernungszulage) — plus die Minutenliste, auf die
 * anschließend die Wochenverteilung und die Zuschlagsbewertung angewendet werden.
 */
function baueTag(
  eingabe: TagEingabe,
  soll: number,
  konfig: EngineKonfig,
  feiertagName: string | null,
  abw: AbwesenheitInfo | null,
  vortagNaechtigung: boolean,
  /** Fehlzeiten in Stunden (AP-22 Teil 4); stehen sie da, gelten sie statt `abw`. */
  liste: { info: AbwesenheitInfo; stunden: number }[] | null = null
): TagIntern {
  const datum = eingabe.datum
  const wt = wochentagVon(datum)
  const istWochenende = wt >= 6
  const istSonntag = wt === 7
  const istFeiertag = feiertagName !== null

  const gesamtBerechnet = rundeStunden(
    dauerStunden(eingabe.kommt, eingabe.geht),
    konfig.rundungSchrittMin,
    konfig.rundungAktiv
  )
  const gesamt = eingabe.gesamtOverride ?? gesamtBerechnet

  // Pause: erfasster Tageswert hat Vorrang; sonst greift die automatische Regel.
  const pauseErfasst = eingabe.pauseMin != null && eingabe.pauseMin >= 0
  const pauseStd = pauseErfasst
    ? (eingabe.pauseMin as number) / 60
    : konfig.pauseAktiv && gesamt > konfig.pauseSchwelleStd
      ? konfig.pauseDauerStd
      : 0
  const naz = eingabe.nazOverride ?? Math.max(0, gesamt - pauseStd)
  const reisezeit = Math.max(0, eingabe.reisezeitStd ?? 0)

  // Soll-Gutschrift nach Ausfallsprinzip: Abwesenheit „als Soll" bzw. gesetzlicher
  // Feiertag. Am gearbeiteten Feiertag bleibt die Gutschrift bestehen; die geleisteten
  // Stunden treiben die Woche über die Schwelle und werden mit 100 % bewertet.
  //
  // Zwei Eingabeformen: **Stunden** je Fehlzeit, mehrere je Tag (Teil 4) — oder der Bestand
  // „ganz/halb" mit einer Art, der genau wie bisher rechnet (ganz = Tagessoll).
  const inStunden = liste && liste.length > 0 ? liste : null
  const summe = (pruefe: (i: AbwesenheitInfo) => boolean) =>
    inStunden ? inStunden.reduce((s, a) => s + (pruefe(a.info) ? a.stunden : 0), 0) : 0
  const abwTage = inStunden
    ? soll > 0
      ? Math.round((summe(() => true) / soll) * 100) / 100
      : 0
    : abw
      ? eingabe.abwesenheitUmfang === 'halb'
        ? 0.5
        : 1
      : 0
  const hatAbwesenheit = inStunden !== null || abw !== null
  const autoFeiertag = !hatAbwesenheit && istFeiertag && soll > 0
  const fehlzeitGutschrift = inStunden
    ? summe((i) => i.alsSoll)
    : abw
      ? abw.alsSoll
        ? soll * abwTage
        : 0
      : autoFeiertag
        ? soll
        : 0
  const zaKonsumiert = inStunden
    ? summe((i) => i.alsSoll && i.zeitausgleich)
    : abw?.zeitausgleich
      ? fehlzeitGutschrift
      : 0
  // Unbezahlte Stunden sind weder geleistet noch gutgeschrieben — und keine Zeitschuld.
  const ohneGutschrift = summe((i) => !i.alsSoll)

  // 24. und 31. Dezember (KV VI/23): Die Arbeitszeit endet vorgezogen, das Entgelt für die
  // Normalarbeitszeit läuft fort. Fortgezahlt wird der Teil des Solls, der nach dem
  // vorgezogenen Ende läge — bemessen ab Betriebsbeginn, denn das Modell führt je Tag nur
  // Stunden. Wer länger bleibt, bekommt dafür den Zuschlag (`bewerteTag`), keine Gutschrift
  // obendrauf; wer fehlt, behält die Minderstunden des Vormittags.
  const betrieb = konfig.betriebszeiten[wt - 1] ?? null
  const halbtagEnde = hhmmZuMinuten(konfig.halbtagEnde)
  const istHalbtag =
    konfig.halbtagAktiv && (datum.endsWith('-12-24') || datum.endsWith('-12-31')) && soll > 0 && !istFeiertag && halbtagEnde !== null
  const bisEnde = istHalbtag && betrieb ? Math.max(0, ((halbtagEnde as number) - (hhmmZuMinuten(betrieb.von) ?? 0)) / 60) : null
  const halbtagGutschrift =
    bisEnde !== null && !hatAbwesenheit ? Math.max(0, Math.min(soll - bisEnde, soll - naz)) : 0

  const sollGutschrift = fehlzeitGutschrift + halbtagGutschrift
  const arbeitSoll = Math.max(0, soll - sollGutschrift)
  const abwesenheiten: TagAbwesenheit[] = inStunden
    ? inStunden.map((a) => ({
        kuerzel: a.info.kuerzel,
        stunden: a.stunden,
        tage: soll > 0 ? Math.round((a.stunden / soll) * 100) / 100 : 0,
        bezahlt: a.info.bezahlt,
        alsSoll: a.info.alsSoll,
      }))
    : abw
      ? [{ kuerzel: abw.kuerzel, stunden: soll * abwTage, tage: abwTage, bezahlt: abw.bezahlt, alsSoll: abw.alsSoll }]
      : []
  const nlz = abwesenheiten.reduce((s, a) => s + (a.bezahlt ? a.stunden : 0), 0) + (autoFeiertag ? soll : 0) + halbtagGutschrift

  // Montagetag ist nur ein Tag mit Abfahrt, Rückfahrt oder Nächtigung. Ein Tag ohne all
  // das ist ein gewöhnlicher Arbeitstag im Betrieb: keine Montagezeit, keine Montage- und
  // keine Entfernungszulage, auch wenn Kommt und Geht erfasst sind. Die Nächtigung des
  // Vortags allein macht keinen Montagetag — der Rückreisetag trägt seine Rückfahrt, und
  // ein leerer Tag nach einer Nächtigung bekäme sonst eine Zulage (Auftraggeber 22.9.2026).
  const montageTag =
    hhmmZuMinuten(eingabe.abfahrt ?? null) !== null ||
    hhmmZuMinuten(eingabe.rueckfahrt ?? null) !== null ||
    eingabe.naechtigung === true

  // Montagezeit: von der Abfahrt (bzw. Kommt) bis zur Rückfahrt (bzw. Geht), abzüglich
  // Fahrzeit und Mittagspause. Die Menge der Montagezulage ist diese Zeit; der €-Wert
  // (Menge × Satz) läuft intern mit.
  const mStart = hhmmZuMinuten(eingabe.abfahrt ?? null) ?? hhmmZuMinuten(eingabe.kommt ?? null)
  const mEnde = hhmmZuMinuten(eingabe.rueckfahrt ?? null) ?? hhmmZuMinuten(eingabe.geht ?? null)
  let montagezeit =
    montageTag && mStart !== null && mEnde !== null ? Math.max(0, (mEnde - mStart) / 60 - reisezeit) : 0
  if (montagezeit > 0) {
    const montagePause = pauseErfasst
      ? pauseStd
      : montagezeit >= konfig.pauseSchwelleStd - EPS
        ? konfig.pauseDauerStd
        : 0
    montagezeit = Math.max(0, montagezeit - montagePause)
  }
  const montagezulageBetrag = Math.round(montagezeit * konfig.montagezulageSatz * 100) / 100

  const spanne = mStart !== null && mEnde !== null ? Math.max(0, (mEnde - mStart) / 60) : 0
  const stufe = montageTag ? ermittleEntfernung(eingabe, montagezeit, konfig, vortagNaechtigung, spanne) : null

  // Persönlicher Termin (Arzt, Behörde …): liegt **außerhalb** von Kommt–Geht, zählt als
  // erfüllte Zeit und wird vom Zeitausgleich abgebucht (Auftraggeber 22.9.2026). Er ist
  // keine geleistete Arbeit — Mehrzeit entsteht aus ihm nie, und er nimmt auch keine weg.
  const termin = Math.max(0, eingabe.terminStunden ?? 0)
  // Bestand: Ein Tag mit Fehlzeit hat nie Minderstunden. In Stunden erfasst, zählt der Rest des Tages.
  const minderstunden =
    soll <= 0 || (abw && !inStunden) ? 0 : Math.max(0, arbeitSoll - naz - termin - ohneGutschrift)

  const e: TagErgebnis = {
    datum,
    wochentag: wt,
    istWochenende,
    istFeiertag,
    feiertagName,
    soll,
    gesamt,
    pausenabzug: pauseStd,
    naz,
    reisezeit,
    normalStunden: sollGutschrift + naz,
    mehrarbeitTeilzeit: 0,
    mehrarbeitVollzeit: 0,
    ueberstunden: 0,
    ueberstundenGewichtet: 0,
    zuschlagStunden: 0,
    zuschlagGewichtet: 0,
    wegzeitAusserhalb: 0,
    wegzeitAusserhalbLenker: 0,
    wegzeitAusserhalbBeifahrer: 0,
    // Auch die Fehlzeit „Zeitausgleich" steht als Minus in der Spalte (Teil 7) — der Saldo
    // des Monats rechnete sie schon immer ab.
    zaTag: minderstunden + termin + zaKonsumiert > 0 ? -(minderstunden + termin + zaKonsumiert) : 0,
    montagezeit,
    montagezulageBetrag,
    entfernungStufe: stufe,
    entfernungBetrag: entfernungBetrag(stufe, konfig, eingabe.verpflegungGestellt === true),
    zuschlaege: [],
    minderstunden,
    abwesenheitKuerzel: inStunden ? inStunden.map((a) => a.info.kuerzel).join(' ') : abw ? abw.kuerzel : null,
    abwesenheitTage: abwTage,
    abwesenheitStunden: inStunden ? sollGutschrift : abw?.alsSoll ? soll * abwTage : 0,
    abwesenheiten,
    nlz,
    zaKonsumiert: zaKonsumiert + termin,
    feiertagBezahlt: autoFeiertag,
    ...(halbtagGutschrift > 0 ? { halbtagGutschrift } : {}),
    terminTitel: eingabe.terminTitel ?? null,
    terminStunden: termin,
  }

  return {
    e,
    minuten: baueMinuten(eingabe, naz, pauseStd, konfig.betriebszeiten[wt - 1] ?? null, vortagNaechtigung),
    arbeit: naz,
    arbeitSoll,
    sollGutschrift,
    gutschriftZaehlt: Math.max(0, sollGutschrift - zaKonsumiert),
    lenker: eingabe.lenker === true,
    istSonntag,
    rueckruf: eingabe.rueckruf === true,
    halbtagAb: istHalbtag ? halbtagEnde : null,
    wocheVorherMin: 0,
    eingabe,
  }
}

/**
 * Bewertet die Minuten eines Tages: je Minute der höchste zutreffende Zuschlagssatz,
 * daraus die rohen und die fertig gerechneten Stunden je Kategorie.
 */
function bewerteTag(ti: TagIntern, konfig: EngineKonfig): void {
  const faktor =
    konfig.umrechnungNenner > 0 ? konfig.umrechnungZaehler / konfig.umrechnungNenner : 1
  const otRegeln = konfig.zuschlagRegeln.filter((r) => r.art === 'ueberstunde_ab')
  const zeitRegeln = konfig.zuschlagRegeln
    .filter((r) => r.art === 'zeit')
    .map((r) => ({
      von: hhmmZuMinuten((r as { von: string }).von) ?? 0,
      bis: hhmmZuMinuten((r as { bis: string }).bis) ?? 0,
      prozent: r.prozent,
      nurUe: (r as { nur?: string }).nur === 'ueberstunden',
    }))
  const istFeiertag = ti.e.istFeiertag
  // AP-22 Teil 6 (K2) — alle vier mit Bestandswert „aus": dann rechnet die Schleife wie zuvor.
  const mehrAlsUe = konfig.mehrarbeitUeber50IstUeberstunde
  const arbeitsfreiAb = konfig.ueberstunde100ArbeitsfreiAbStunde
  const arbeitsfrei = arbeitsfreiAb > 0 && ti.e.soll <= EPS && !ti.istSonntag && !istFeiertag
  const wocheAb = konfig.ueberstunde100AbWochenstunde
  const jeSatz = new Map<number, { stunden: number; gewichtet: number }>()

  let ueMin = 0
  let ueGew = 0
  let zuMin = 0
  let zuGew = 0
  let mehrTz = 0
  let mehrVz = 0
  let normalMin = 0
  let wegAus = 0
  let ueZaehler = 0 // laufende Nummer der Überstunden-Minute (für „ab X. Überstunde")
  let mehrzeitZaehler = 0 // dasselbe samt der Mehrarbeit 38,5–40 h des Tages (KV XIV/8.6)
  let arbeitIndex = 0 // laufende Arbeitsminute des Tages
  // Zulagen, die sich an einem Uhrzeitfenster bemessen (K3) — gezählt wird in der Schleife.
  const fenster = konfig.zulagen
    .filter((z) => z.bemessung === 'fensterstunden_ohne_ueberstunden')
    .map((z) => ({ schluessel: z.schluessel, von: hhmmZuMinuten(z.von ?? null) ?? 0, bis: hhmmZuMinuten(z.bis ?? null) ?? 0, minuten: 0 }))

  for (const m of ti.minuten) {
    if (m.weg && m.ausserhalb) wegAus++
    // Wegzeit außerhalb der Betriebszeit: beim Lenker Überstunde — das hat die
    // Verteilung schon erledigt (`belegeBaender`), soweit Mehrzeit anliegt. Hier wurde
    // sie bis 30.9.2026 noch einmal zur Überstunde gemacht, zusätzlich zur Mehrzeit.
    // Ohne Lenker normale Arbeitsstunde ohne Zuschlag (KV Metallgewerbe VIII/7).
    const wegOhneZuschlag = m.weg && !ti.lenker
    // Mit `mehrAlsUe` wird eine Mehrarbeitsminute zunächst **wie** eine Überstunde bewertet:
    // Stünde ihr damit mehr als 50 % zu, ist sie eine (KV VIa).
    const alsUe = m.eimer === 'ue' || (mehrAlsUe && m.eimer === 'mehr_vz')

    let p = 0 // Sätze, die jeder Arbeitsminute zustehen
    let pUe = 0 // Sätze, die nur einer Überstunde zustehen
    if (alsUe) {
      pUe = konfig.zuschlagUeberstunde
      if (arbeitsfrei) {
        // Arbeitsfreier Tag: 100 % erst ab der x. Arbeitsstunde, nicht ab der dritten Überstunde (KV XIV/8.3).
        if (arbeitIndex / 60 >= arbeitsfreiAb - 1) pUe = Math.max(pUe, 100)
      } else {
        const ordinal = (konfig.ueberstundeAbZaehltMehrarbeit ? mehrzeitZaehler : ueZaehler) / 60
        for (const r of otRegeln) {
          if (r.art === 'ueberstunde_ab' && ordinal >= r.abStunde - 1) pUe = Math.max(pUe, r.prozent)
        }
      }
      // Ab der x. Arbeitsstunde der Kalenderwoche (KV XIV/8.5).
      if (wocheAb > 0 && (ti.wocheVorherMin + arbeitIndex) / 60 >= wocheAb - 1) pUe = Math.max(pUe, 100)
      if (ti.rueckruf) pUe = Math.max(pUe, konfig.rueckrufProzent)
    }
    if (!wegOhneZuschlag) {
      if (m.clock !== null) {
        for (const r of zeitRegeln) {
          if (!imFensterMin(m.clock, r.von, r.bis)) continue
          if (!r.nurUe) p = Math.max(p, r.prozent)
          else if (alsUe) pUe = Math.max(pUe, r.prozent)
        }
      }
      if (m.ausserhalb) p = Math.max(p, konfig.zuschlagAusserhalbBetriebszeit)
    }
    if (ti.istSonntag) p = Math.max(p, konfig.sonntagProzent)
    if (istFeiertag) p = Math.max(p, konfig.feiertagProzent)
    // 24./31.12.: Was nach dem vorgezogenen Arbeitsende gearbeitet wird (KV VI/23, XIV/8.9).
    if (ti.halbtagAb !== null && m.clock !== null && m.clock >= ti.halbtagAb) p = Math.max(p, konfig.halbtagProzent)

    // Mehrarbeit mit mehr als 50 % wird Überstunde — sonst bleibt sie Mehrarbeit, ohne den Überstundensatz.
    if (m.eimer === 'mehr_vz' && mehrAlsUe && Math.max(p, pUe) > 50) m.eimer = 'ue'
    const istUe = m.eimer === 'ue'
    if (istUe) {
      p = Math.max(p, pUe)
      ueZaehler++
    }
    if (istUe || m.eimer === 'mehr_vz') mehrzeitZaehler++
    arbeitIndex++
    if (!istUe && m.clock !== null) {
      for (const f of fenster) if (imFensterMin(m.clock, f.von, f.bis)) f.minuten++
    }

    const gew = (faktor * (1 + p / 100)) / 60
    if (p > 0) {
      const eintrag = jeSatz.get(p) ?? { stunden: 0, gewichtet: 0 }
      eintrag.stunden += 1 / 60
      eintrag.gewichtet += gew
      jeSatz.set(p, eintrag)
    }
    // Mehrzeit-Eimer gehen vor: eine Mehrarbeitsstunde bleibt Mehrarbeit, auch wenn
    // sie zuschlagspflichtig ist (Userentscheid 2026-09-06). Ihr Zuschlagssatz steht
    // trotzdem in `zuschlaege` — diese Aufstellung überschneidet sich also bewusst
    // mit den Spalten Mehrarbeit/Überstunden. `zuschlagStunden` zählt nur noch
    // zuschlagspflichtige **Normal**stunden, damit die Spaltensumme
    // Normal + Mehrarbeit + ÜS + Zuschlagsstunden = geleistete Zeit bleibt.
    if (istUe) {
      ueMin++
      ueGew += gew
    } else if (m.eimer === 'mehr_vz') mehrVz++
    else if (m.eimer === 'mehr_tz') mehrTz++
    else if (p > 0) {
      zuMin++
      zuGew += gew
    } else normalMin++
  }

  const zuschlaege: ZuschlagAnteil[] = [...jeSatz.entries()]
    .map(([prozent, v]) => ({ prozent, stunden: v.stunden, gewichtet: v.gewichtet }))
    .sort((a, b) => b.prozent - a.prozent)

  const e = ti.e
  e.ueberstunden = ueMin / 60
  e.ueberstundenGewichtet = ueGew
  e.zuschlagStunden = zuMin / 60
  e.zuschlagGewichtet = zuGew
  e.mehrarbeitVollzeit = mehrVz / 60
  e.mehrarbeitTeilzeit = mehrTz / 60
  e.wegzeitAusserhalb = wegAus / 60
  e.wegzeitAusserhalbLenker = ti.lenker ? wegAus / 60 : 0
  e.wegzeitAusserhalbBeifahrer = ti.lenker ? 0 : wegAus / 60
  e.normalStunden = ti.sollGutschrift + normalMin / 60 + e.terminStunden
  e.zuschlaege = zuschlaege

  // ── Zulagen (AP-22 Teil 6, K3) ────────────────────────────────────────────
  if (konfig.zulagen.length > 0) {
    const roh = ti.eingabe.zulagen ?? {}
    const menge = new Map<string, number>()
    for (const z of konfig.zulagen) {
      const w = roh[z.schluessel]
      let m = 0
      switch (z.bemessung) {
        case 'erfasste_stunden':
          // Nur für die Stunden, in denen die Arbeit anfällt — also höchstens die Arbeitszeit.
          m = typeof w === 'number' && w > 0 ? Math.min(w, ti.arbeit) : 0
          break
        case 'markierte_tage_naz':
          m = w === true ? ti.arbeit : 0
          break
        case 'fensterstunden_ohne_ueberstunden':
          m = (fenster.find((f) => f.schluessel === z.schluessel)?.minuten ?? 0) / 60
          break
        case 'naechtigung':
          m = ti.eingabe.naechtigung === true ? 1 : 0
          break
        case 'kilometer':
          m = Math.max(0, ti.eingabe.km ?? 0)
          break
      }
      menge.set(z.schluessel, m)
    }
    const liste: TagZulage[] = []
    for (const z of konfig.zulagen) {
      let m = menge.get(z.schluessel) ?? 0
      if (z.entfaelltBei) m = Math.max(0, m - (menge.get(z.entfaelltBei) ?? 0))
      if (m <= EPS) continue
      liste.push({
        schluessel: z.schluessel,
        menge: Math.round(m * 10000) / 10000,
        betrag: z.prozent ? 0 : Math.round(m * z.satz * 100) / 100,
      })
    }
    if (liste.length > 0) e.zulagen = liste
  }
}

/**
 * Markiert die letzten `anzahl` noch freien (= `normal`) Minuten der Liste mit
 * `eimer` und liefert zurück, wie viele Minuten nicht mehr untergebracht wurden.
 * Aufeinanderfolgende Aufrufe stapeln sich dadurch von oben nach unten.
 */
function belegeFrei(liste: Minute[], anzahl: number, eimer: Eimer): number {
  let rest = anzahl
  for (let i = liste.length - 1; i >= 0 && rest > 0; i--) {
    if (liste[i].eimer !== 'normal') continue
    liste[i].eimer = eimer
    rest--
  }
  return rest
}

/** Mengen je Eimer für einen Abrechnungszeitraum. */
interface Baender {
  ue: number
  mehrVz: number
  mehrTz: number
}

/**
 * Bänder nach Wochen-/Periodenlogik: Mehrzeit beginnt über den vereinbarten
 * Stunden, ab der Mehrarbeitsschwelle mit dem höheren Satz, ab der
 * Überstundenschwelle als Überstunde.
 */
function baenderSaldo(geleistet: number, vereinbart: number, sMehr: number, sUe: number): Baender {
  const untereGrenze = Math.min(vereinbart, sMehr)
  return {
    ue: Math.max(0, geleistet - sUe),
    mehrVz: Math.max(0, Math.min(geleistet, sUe) - Math.max(untereGrenze, sMehr)),
    mehrTz: Math.max(0, Math.min(geleistet, sMehr) - untereGrenze),
  }
}

/** Was ein Tag für die Wochenschwelle zählt: Arbeit und bezahlte Gutschrift (Ausfallsprinzip). */
function zaehlt(t: TagIntern): number {
  return t.arbeit + t.gutschriftZaehlt
}

/** Mehrzeit des Tages im Tagesmodus: was über dem Tages-Soll liegt — höchstens die Arbeit selbst. */
function ueberSollStd(t: TagIntern): number {
  return Math.min(t.arbeit, Math.max(0, zaehlt(t) - t.e.soll))
}

/**
 * Bänder im Tagesmodus: Mehrzeit ist die Summe der Stunden über dem jeweiligen
 * Tages-Soll, aufgeteilt **in ihrer Reihenfolge** — zuerst die Mehrarbeit in der
 * Breite ihrer Bänder (Teilzeit: vereinbart … 38,5 h mit 25 %; dann 38,5 … 40 h mit
 * 50 %), alles Weitere Überstunde. Die Wochensumme entscheidet nicht mehr mit: Ein
 * Tag ändert sich nicht dadurch, dass später in der Woche etwas dazukommt oder fehlt
 * (AP-22 Teil 5; bis dahin `min(Mehrzeit, Wochensumme − 40)`).
 */
function baenderMehrzeit(
  mehrzeit: number,
  vereinbart: number,
  sMehr: number,
  sUe: number,
  teilzeit: boolean
): Baender {
  const unten = Math.min(vereinbart, sUe)
  const breiteTz = teilzeit ? Math.max(0, sMehr - unten) : 0
  const breiteVz = Math.max(0, sUe - (teilzeit ? Math.max(unten, sMehr) : unten))
  const mehrTz = Math.min(mehrzeit, breiteTz)
  const mehrVz = Math.min(mehrzeit - mehrTz, breiteVz)
  return { ue: mehrzeit - mehrTz - mehrVz, mehrVz, mehrTz }
}

/** Was von den Bändern `gesamt` übrig ist, nachdem `verbraucht` schon vergeben wurde. */
function baenderRest(gesamt: Baender, verbraucht: Baender): Baender {
  return {
    ue: Math.max(0, gesamt.ue - verbraucht.ue),
    mehrVz: Math.max(0, gesamt.mehrVz - verbraucht.mehrVz),
    mehrTz: Math.max(0, gesamt.mehrTz - verbraucht.mehrTz),
  }
}

/** Wegzeit-Minuten des Lenkers außerhalb der Betriebszeit — sie werden Überstunde (VIII/7). */
function lenkerWeg(tage: TagIntern[]): Minute[] {
  const out: Minute[] = []
  for (const t of tage) if (t.lenker) for (const m of t.minuten) if (m.weg && m.ausserhalb) out.push(m)
  return out
}

/**
 * Die Minuten, die je Tag über dem Tages-Soll liegen — chronologisch über die
 * ganze Gruppe. Grundlage der Zuordnung im Tagesmodus; die Bemessung entspricht
 * `ueberSollStd`.
 *
 * Die Lenker-Wegzeit des Tages gehört zuerst dazu (die späteste zuerst), der Rest
 * sind wie bisher die letzten Minuten des Tages. Die Menge je Tag bleibt NAZ − Soll —
 * sonst zählte eine Wegzeit am Morgen einmal als Soll und ein zweites Mal als
 * Überstunde (Befund 24.9.2026).
 */
function ueberSollMinuten(gruppe: TagIntern[]): { weg: Minute[]; kandidaten: Minute[] } {
  const weg: Minute[] = []
  const kandidaten: Minute[] = []
  for (const t of gruppe) {
    let rest = Math.min(t.minuten.length, Math.round(ueberSollStd(t) * 60))
    if (rest <= 0) continue
    const wegTag = lenkerWeg([t])
    const gewaehlt = new Set(wegTag.slice(Math.max(0, wegTag.length - rest)))
    rest -= gewaehlt.size
    for (let i = t.minuten.length - 1; i >= 0 && rest > 0; i--) {
      if (gewaehlt.has(t.minuten[i])) continue
      gewaehlt.add(t.minuten[i])
      rest--
    }
    for (const m of t.minuten) {
      if (!gewaehlt.has(m)) continue
      kandidaten.push(m)
      if (t.lenker && m.weg && m.ausserhalb) weg.push(m)
    }
  }
  return { weg, kandidaten }
}

/**
 * Belegt die Bänder auf `ziel`, nachdem die Lenker-Wegzeit `weg` Überstunde geworden
 * ist. Sie zählt in die Überstunden des Bandes; reicht sie darüber hinaus, geht der
 * Überhang von der Mehrarbeit ab (erst 50 %, dann 25 %) — die Mehrzeit insgesamt
 * bleibt, wie die Bänder sie bemessen haben. Liefert die Überstunden-Minuten, die in
 * `ziel` keinen Platz mehr fanden.
 */
function belegeBaender(ziel: Minute[], weg: Minute[], b: Baender): number {
  for (const m of weg) m.eimer = 'ue'
  let ue = Math.round(b.ue * 60) - weg.length
  let mehrVz = Math.round(b.mehrVz * 60)
  let mehrTz = Math.round(b.mehrTz * 60)
  if (ue < 0) {
    const ueberhang = -ue
    const abVz = Math.min(ueberhang, mehrVz)
    mehrVz -= abVz
    mehrTz = Math.max(0, mehrTz - (ueberhang - abVz))
    ue = 0
  }
  const offen = belegeFrei(ziel, ue, 'ue')
  belegeFrei(ziel, mehrVz, 'mehr_vz')
  belegeFrei(ziel, mehrTz, 'mehr_tz')
  return offen
}

/**
 * Wochen- und Periodenmodus: Die Lenker-Wegzeit wird Überstunde, soweit die Mehrzeit
 * des Zeitraums reicht (die späteste zuerst) — mehr Mehrzeit, als die Bänder
 * bemessen, entsteht dadurch nicht.
 */
function wegImRahmen(tage: TagIntern[], b: Baender): Minute[] {
  const rahmen = Math.round((b.ue + b.mehrVz + b.mehrTz) * 60)
  const weg = lenkerWeg(tage)
  return weg.slice(Math.max(0, weg.length - rahmen))
}

/**
 * Verteilt Mehrarbeit & Überstunden innerhalb einer Kalenderwoche — je nach
 * Einstellung nach Wochen- oder Tageslogik.
 *
 * `gruppe` sind die Tage der Woche **im Monat**, `vor` die Tage derselben Woche im
 * Vormonat (AP-22 Teil 5). Die Schwellen gelten für die ganze Woche; was `vor` für
 * sich schon an Bändern verbraucht hat, wird abgezogen, und nur der Rest landet auf
 * den Minuten des Monats. Beide Monate zusammen ergeben so die Woche in einem Stück.
 */
function verteileWoche(
  gruppe: TagIntern[],
  vor: TagIntern[],
  kontext: MitarbeiterKontext,
  konfig: EngineKonfig,
  wochenSollVoll: (datum: string) => number,
  teilzeit: boolean
): void {
  if (gruppe.reduce((a, t) => a + t.arbeit, 0) <= EPS) return

  const sMehr = konfig.mehrarbeitSchwelle
  const sUe = konfig.ueberstundenSchwelle
  const vereinbart = kontext.wochenstunden ?? wochenSollVoll(gruppe[0].e.datum)

  const taeglich = konfig.ueberstundenZeitraum === 'taeglich'
  const baender = (tage: TagIntern[]): Baender =>
    taeglich
      ? baenderMehrzeit(tage.reduce((a, t) => a + ueberSollStd(t), 0), vereinbart, sMehr, sUe, teilzeit)
      : baenderSaldo(tage.reduce((a, t) => a + zaehlt(t), 0), vereinbart, sMehr, sUe)
  const b = vor.length > 0 ? baenderRest(baender([...vor, ...gruppe]), baender(vor)) : baender(gruppe)

  // Minuten der Woche chronologisch — die zuletzt geleisteten sind die Überstunden.
  const flach: Minute[] = []
  for (const t of gruppe) for (const m of t.minuten) flach.push(m)

  if (!taeglich) {
    belegeBaender(flach, wegImRahmen(gruppe, b), b)
    return
  }

  // Tagesmodus: die Mehrzeit sitzt dort, wo sie entstanden ist — auf den Minuten
  // über dem jeweiligen Tages-Soll.
  const { weg, kandidaten } = ueberSollMinuten(gruppe)
  const offen = belegeBaender(kandidaten, weg, b)
  if (offen > 0) belegeFrei(flach, offen, 'ue')
}

/**
 * Monats- bzw. mehrmonatige Abrechnung: die Wochenschwellen werden auf die
 * Periode hochgerechnet (Soll der Periode ÷ durchschnittliches Wochen-Soll);
 * ausgewiesen wird nur der Saldo am Periodenende. `kontext.periodeVorher`
 * liefert bei mehrmonatigen Perioden die bereits abgerechneten Vormonate —
 * dieser Monat zeigt dann nur den Zuwachs.
 *
 * OFFEN (Userentscheid 2026-09-06, ausdrücklich „noch nicht ganz sicher"): ob in
 * diesen Modi zusätzlich Tages- oder Wochenspitzen sofort als Überstunden gelten
 * sollen. Derzeit zählt ausschließlich der Saldo.
 */
function verteilePeriode(
  intern: TagIntern[],
  kontext: MitarbeiterKontext,
  konfig: EngineKonfig,
  sollSchnitt: number
): void {
  const vorher = kontext.periodeVorher
  const geleistet = (vorher?.geleistet ?? 0) + intern.reduce((a, t) => a + t.arbeit, 0)
  const soll = (vorher?.soll ?? 0) + intern.reduce((a, t) => a + t.e.soll, 0)
  if (geleistet <= EPS) return

  // Länge der Periode in „vollen Wochen" — daran werden die Schwellen skaliert.
  const wochen = sollSchnitt > EPS && soll > EPS ? soll / sollSchnitt : intern.length / 7
  const sMehr = konfig.mehrarbeitSchwelle * wochen
  const sUe = konfig.ueberstundenSchwelle * wochen
  const vereinbart = (kontext.wochenstunden ?? sollSchnitt) * wochen

  const periode = baenderSaldo(geleistet, vereinbart, sMehr, sUe)
  const b: Baender = {
    ue: Math.max(0, periode.ue - (vorher?.ueberstunden ?? 0)),
    mehrVz: Math.max(0, periode.mehrVz - (vorher?.mehrarbeitVollzeit ?? 0)),
    mehrTz: Math.max(0, periode.mehrTz - (vorher?.mehrarbeitTeilzeit ?? 0)),
  }

  const flach: Minute[] = []
  for (const t of intern) for (const m of t.minuten) flach.push(m)
  belegeBaender(flach, wegImRahmen(intern, b), b)
}

/**
 * Stundenrahmen mit Mehrzeit (AP-22 Teil 7): Der Wochenrahmen verteilt je Kalenderwoche
 * wie der Wochenmodus — auch über die Monatsgrenze —, der Monatsrahmen als Saldo des
 * Monats mit den auf den Monat hochgerechneten Schwellen. Der Überstunden-Zeitraum des
 * Betriebs gilt hier nicht: Ohne Tages-Soll machte der Tagesmodus jede Stunde zu Mehrzeit.
 */
function verteileRahmen(
  intern: TagIntern[],
  vorIntern: TagIntern[],
  ersterMontag: string,
  rahmen: NonNullable<MitarbeiterKontext['rahmen']>,
  kontext: MitarbeiterKontext,
  konfig: EngineKonfig,
  wochenSollVoll: (datum: string) => number,
  teilzeit: boolean
): void {
  if (rahmen.zeitraum === 'monat') {
    // `verteilePeriode` rechnet ohne Soll mit Kalendertagen ÷ 7 Wochen — der Rahmen ist die
    // vereinbarte Menge des ganzen Monats.
    const wochenstunden = (rahmen.stunden * 7) / intern.length
    verteilePeriode(intern, { ...kontext, wochenstunden, periodeVorher: undefined }, konfig, 0)
    return
  }
  const wochenweise: EngineKonfig = { ...konfig, ueberstundenZeitraum: 'woechentlich' }
  const mitRahmen: MitarbeiterKontext = { ...kontext, wochenstunden: rahmen.stunden }
  for (const [montag, gruppe] of nachWochen(intern)) {
    verteileWoche(gruppe, montag === ersterMontag ? vorIntern : [], mitRahmen, wochenweise, wochenSollVoll, teilzeit)
  }
}

/** Tage nach dem Montag ihrer Kalenderwoche, in ihrer Reihenfolge. */
function nachWochen(tage: TagIntern[]): Map<string, TagIntern[]> {
  const wochen = new Map<string, TagIntern[]>()
  for (const t of tage) {
    const key = montagKey(t.e.datum)
    const liste = wochen.get(key)
    if (liste) liste.push(t)
    else wochen.set(key, [t])
  }
  return wochen
}

/** Was ein Tag zum Rahmen beiträgt: Arbeit, Gutschrift aus Fehlzeiten, persönlicher Termin. */
function rahmenIst(t: TagIntern): number {
  return t.arbeit + t.sollGutschrift + t.e.terminStunden
}

function letzterTag(jahr: number, monat: number): string {
  return `${jahr}-${zpad(monat)}-${zpad(tageImMonat(jahr, monat))}`
}

/**
 * Soll, Ist und Differenz je Zeitraum des Rahmens. Eine Kalenderwoche gehört zum Monat
 * ihres Sonntags (T7-4): Die erste Woche zählt die Tage des Vormonats mit, die letzte
 * läuft noch, wenn sie erst im Folgemonat endet.
 */
function rahmenPerioden(
  intern: TagIntern[],
  vorIntern: TagIntern[],
  rahmen: NonNullable<MitarbeiterKontext['rahmen']>,
  letzter: string
): RahmenPeriode[] {
  const runde = (n: number) => Math.round(n * 100) / 100
  const periode = (von: string, bis: string, tage: TagIntern[]): RahmenPeriode => {
    const ist = runde(tage.reduce((a, t) => a + rahmenIst(t), 0))
    return { von, bis, soll: rahmen.stunden, ist, differenz: runde(ist - rahmen.stunden), abgeschlossen: bis <= letzter }
  }
  if (rahmen.zeitraum === 'monat') return [periode(intern[0].e.datum, letzter, intern)]
  const erster = intern[0].e.datum
  return [...nachWochen(intern)].map(([montag, gruppe]) => {
    let sonntag = montag
    for (let i = 0; i < 6; i++) sonntag = folgetag(sonntag)
    return periode(montag, sonntag, montag < erster ? [...vorIntern, ...gruppe] : gruppe)
  })
}

/**
 * Berechnet einen ganzen Monat: Tageszeilen, wöchentliche Mehrarbeit/Überstunden,
 * Zuschlagsbewertung und die Monatssummen/-zähler.
 */
export function berechneMonat(
  jahr: number,
  monat: number,
  eintraege: TagEingabe[],
  kontext: MitarbeiterKontext,
  konfig: EngineKonfig,
  feiertage: ReadonlyMap<string, string>,
  abwesenheiten: ReadonlyMap<AbwesenheitId, AbwesenheitInfo>
): MonatsErgebnis {
  const rahmen = kontext.rahmen
  // Stundenrahmen (Teil 7): kein Tages-Soll — Feiertag und Fehlzeit bringen nur, was erfasst ist.
  const tagesSoll = rahmen ? () => 0 : sollLookup(kontext)
  const wochenSollVoll = wochenSollLookup(kontext)
  const eingabeMap = new Map(eintraege.map((e) => [e.datum, e]))
  const anzahl = tageImMonat(jahr, monat)

  const erster = `${jahr}-${zpad(monat)}-01`

  /** Nächtigung des Vortags; für den 1. des Monats aus dem Kontext (Vormonat). */
  function vortagNaechtigung(datum: string): boolean {
    if (datum === erster) return kontext.vortagNaechtigung === true
    const d = new Date(
      Date.UTC(Number(datum.slice(0, 4)), Number(datum.slice(5, 7)) - 1, Number(datum.slice(8, 10)))
    )
    d.setUTCDate(d.getUTCDate() - 1)
    return eingabeMap.get(d.toISOString().slice(0, 10))?.naechtigung === true
  }

  function tagIntern(eingabe: TagEingabe, vortag: boolean): TagIntern {
    const datum = eingabe.datum
    const abw =
      eingabe.abwesenheitId != null ? (abwesenheiten.get(eingabe.abwesenheitId) ?? null) : null
    // Teil 4: Fehlzeiten in Stunden — unbekannte Arten und leere Zeilen fallen weg.
    const liste = (eingabe.abwesenheiten ?? [])
      .map((a) => ({ info: abwesenheiten.get(a.id), stunden: a.stunden }))
      .filter((a): a is { info: AbwesenheitInfo; stunden: number } => !!a.info && a.stunden > 0)
    return baueTag(eingabe, tagesSoll(datum), konfig, feiertage.get(datum) ?? null, abw, vortag, liste)
  }

  const intern: TagIntern[] = []
  for (let d = 1; d <= anzahl; d++) {
    const datum = `${jahr}-${zpad(monat)}-${zpad(d)}`
    intern.push(tagIntern(eingabeMap.get(datum) ?? { datum }, vortagNaechtigung(datum)))
  }

  // Die Tage des Vormonats ab dem Montag der ersten Woche (AP-22 Teil 5) — jeder Tag,
  // auch der ohne Zeile: Ein Feiertag zählt für die Woche mit. Die Nächtigung des
  // Vortags betrifft nur die Entfernungszulage und ist hier ohne Belang.
  const vorIntern: TagIntern[] = []
  const ersterMontag = montagKey(erster)
  if (kontext.vorwoche) {
    const vorMap = new Map(kontext.vorwoche.map((e) => [e.datum, e]))
    for (let datum = ersterMontag; datum < erster; datum = folgetag(datum)) {
      vorIntern.push(tagIntern(vorMap.get(datum) ?? { datum }, false))
    }
  }

  // ── Verteilung von Mehrarbeit & Überstunden nach eingestelltem Zeitraum ────
  const teilzeit = istTeilzeit(kontext, konfig)
  const zeitraum = konfig.ueberstundenZeitraum
  if (rahmen) {
    if (rahmen.mehrzeit) verteileRahmen(intern, vorIntern, ersterMontag, rahmen, kontext, konfig, wochenSollVoll, teilzeit)
  } else if (zeitraum === 'monatlich' || zeitraum === 'monate') {
    verteilePeriode(intern, kontext, konfig, wochenSollSchnitt(kontext))
  } else {
    for (const [montag, gruppe] of nachWochen(intern)) {
      verteileWoche(gruppe, montag === ersterMontag ? vorIntern : [], kontext, konfig, wochenSollVoll, teilzeit)
    }
  }

  // Laufende Arbeitsminuten der Kalenderwoche vor jedem Tag (KV XIV/8.5) — die Woche beginnt
  // am Monatsersten mit dem, was der Vormonat ab ihrem Montag schon geleistet hat.
  const wochenLauf = new Map<string, number>()
  for (const t of intern) {
    const montag = montagKey(t.e.datum)
    const bisher =
      wochenLauf.get(montag) ?? (montag === ersterMontag ? vorIntern.reduce((a, v) => a + v.minuten.length, 0) : 0)
    t.wocheVorherMin = bisher
    wochenLauf.set(montag, bisher + t.minuten.length)
  }

  for (const t of intern) bewerteTag(t, konfig)

  // Der Termin zählt zur ausgewiesenen Zeit des Tages — erst nach der Verteilung, weil er
  // für Mehrzeit und Zuschläge keine Arbeitsminute ist.
  for (const t of intern) {
    if (t.e.terminStunden <= 0) continue
    t.e.gesamt += t.e.terminStunden
    t.e.naz += t.e.terminStunden
  }

  const tage = intern.map((t) => t.e)

  // ── Summen & Zähler ───────────────────────────────────────────────────────
  const summen: MonatsErgebnis['summen'] = {
    gesamt: 0,
    naz: 0,
    geleistet: 0,
    soll: 0,
    normalStunden: 0,
    mehrarbeitTeilzeit: 0,
    mehrarbeitVollzeit: 0,
    ueberstunden: 0,
    ueberstundenGewichtet: 0,
    zuschlagStunden: 0,
    zuschlagGewichtet: 0,
    wegzeitAusserhalb: 0,
    wegzeitAusserhalbLenker: 0,
    wegzeitAusserhalbBeifahrer: 0,
    zaSaldo: 0,
    zaKonsumiert: 0,
    nlz: 0,
    reisezeit: 0,
    minderstunden: 0,
    km: 0,
    montagezeit: 0,
    terminStunden: 0,
    zuschlagJeSatz: {},
  }
  const zaehler: MonatsErgebnis['zaehler'] = {
    lenkerTage: 0,
    montagezulageTage: 0,
    montagezulageBetrag: 0,
    entfernungszulageTage: 0,
    entfernungszulageBetrag: 0,
    entfernungTage: { klein: 0, mittel: 0, gross: 0 },
    naechtigungen: 0,
    feiertagTage: 0,
    abwesenheitTage: {},
    abwesenheitStunden: {},
    nlz: {},
  }

  let geleistetGesamt = 0
  for (let i = 0; i < intern.length; i++) {
    const ti = intern[i]
    const t = ti.e
    summen.gesamt += t.gesamt
    summen.naz += t.naz
    summen.geleistet += ti.arbeit
    summen.soll += t.soll
    summen.normalStunden += t.normalStunden
    summen.mehrarbeitTeilzeit += t.mehrarbeitTeilzeit
    summen.mehrarbeitVollzeit += t.mehrarbeitVollzeit
    summen.ueberstunden += t.ueberstunden
    summen.ueberstundenGewichtet += t.ueberstundenGewichtet
    summen.zuschlagStunden += t.zuschlagStunden
    summen.zuschlagGewichtet += t.zuschlagGewichtet
    summen.wegzeitAusserhalb += t.wegzeitAusserhalb
    summen.wegzeitAusserhalbLenker += t.wegzeitAusserhalbLenker
    summen.wegzeitAusserhalbBeifahrer += t.wegzeitAusserhalbBeifahrer
    summen.zaKonsumiert += t.zaKonsumiert
    summen.nlz += t.nlz
    summen.reisezeit += t.reisezeit
    summen.minderstunden += t.minderstunden
    summen.terminStunden += t.terminStunden
    summen.montagezeit += t.montagezeit
    geleistetGesamt += ti.arbeit + ti.sollGutschrift + t.terminStunden

    zaehler.montagezulageBetrag += t.montagezulageBetrag
    if (t.montagezeit > 0) zaehler.montagezulageTage += 1
    if (t.entfernungStufe) {
      zaehler.entfernungTage[t.entfernungStufe] += 1
      zaehler.entfernungszulageTage += 1
      zaehler.entfernungszulageBetrag += t.entfernungBetrag
    }
    for (const z of t.zuschlaege) {
      const vor = summen.zuschlagJeSatz[z.prozent] ?? { stunden: 0, gewichtet: 0 }
      summen.zuschlagJeSatz[z.prozent] = {
        stunden: vor.stunden + z.stunden,
        gewichtet: vor.gewichtet + z.gewichtet,
      }
    }

    const eingabe = eingabeMap.get(t.datum)
    if (eingabe) {
      if (eingabe.lenker) zaehler.lenkerTage += 1
      if (eingabe.naechtigung) zaehler.naechtigungen += 1
      summen.km += eingabe.km ?? 0
    }
    // Je Fehlzeit des Tages: Tage und Stunden unter dem Kürzel ihrer Art. Die Bestandsform
    // (ganz/halb) zählt Stunden wie bisher nur, wo die Art als Soll gilt.
    const bestand = !(eingabe?.abwesenheiten && eingabe.abwesenheiten.length > 0)
    for (const a of t.abwesenheiten) {
      zaehler.abwesenheitTage[a.kuerzel] = (zaehler.abwesenheitTage[a.kuerzel] ?? 0) + a.tage
      zaehler.abwesenheitStunden[a.kuerzel] =
        (zaehler.abwesenheitStunden[a.kuerzel] ?? 0) + (bestand && !a.alsSoll ? 0 : a.stunden)
      if (a.bezahlt) zaehler.nlz[a.kuerzel] = (zaehler.nlz[a.kuerzel] ?? 0) + a.stunden
    }
    if (t.feiertagBezahlt) {
      zaehler.feiertagTage += 1
      zaehler.nlz[NLZ_FEIERTAG] = (zaehler.nlz[NLZ_FEIERTAG] ?? 0) + t.soll
    }
    if (t.halbtagGutschrift) zaehler.nlz[NLZ_HALBTAG] = (zaehler.nlz[NLZ_HALBTAG] ?? 0) + t.halbtagGutschrift
    for (const z of t.zulagen ?? []) {
      const je = (zaehler.zulagen ??= {})
      const vor = je[z.schluessel] ?? { menge: 0, betrag: 0, tage: 0 }
      je[z.schluessel] = { menge: vor.menge + z.menge, betrag: vor.betrag + z.betrag, tage: vor.tage + 1 }
    }
  }
  for (const z of Object.values(zaehler.zulagen ?? {})) {
    z.menge = Math.round(z.menge * 10000) / 10000
    z.betrag = Math.round(z.betrag * 100) / 100
  }

  // Zeitausgleich: die Differenz Soll ↔ geleistet fließt direkt aufs ZA-Konto,
  // abzüglich des im Monat konsumierten Zeitausgleichs. Kein Übertrag über Monate.
  summen.zaSaldo = geleistetGesamt - summen.soll - summen.zaKonsumiert
  if (rahmen) {
    const perioden = rahmenPerioden(intern, vorIntern, rahmen, letzterTag(jahr, monat))
    const zu = perioden.filter((p) => p.abgeschlossen)
    summen.rahmen = { ...rahmen, perioden }
    summen.soll = zu.reduce((a, p) => a + p.soll, 0)
    // Nur Kontrolle: keine Zeitschuld und kein Guthaben (T7-5).
    summen.minderstunden = rahmen.mehrzeit ? zu.reduce((a, p) => a + Math.max(0, -p.differenz), 0) : 0
    summen.zaSaldo = rahmen.mehrzeit ? zu.reduce((a, p) => a + p.differenz, 0) - summen.zaKonsumiert : 0
  }
  zaehler.montagezulageBetrag = Math.round(zaehler.montagezulageBetrag * 100) / 100
  zaehler.entfernungszulageBetrag = Math.round(zaehler.entfernungszulageBetrag * 100) / 100

  return { jahr, monat, tage, summen, zaehler }
}

/**
 * Erster Monat der Abrechnungsperiode, in die `monat` fällt. Die Perioden
 * beginnen im Jänner (bei 3 Monaten also 1, 4, 7, 10), damit sie nie über einen
 * Jahreswechsel laufen.
 */
export function periodeStartMonat(monat: number, laenge: number): number {
  const n = Math.max(1, Math.round(laenge))
  return Math.floor((monat - 1) / n) * n + 1
}

/**
 * Fasst die bereits gerechneten Vormonate einer Periode zu einem Übertrag
 * zusammen, den `berechneMonat` über `kontext.periodeVorher` entgegennimmt.
 */
export function periodeUebertrag(vormonate: MonatsErgebnis[]): PeriodeVorher {
  const uebertrag: PeriodeVorher = {
    geleistet: 0,
    soll: 0,
    ueberstunden: 0,
    mehrarbeitVollzeit: 0,
    mehrarbeitTeilzeit: 0,
  }
  for (const m of vormonate) {
    uebertrag.geleistet += m.summen.geleistet
    uebertrag.soll += m.summen.soll
    uebertrag.ueberstunden += m.summen.ueberstunden
    uebertrag.mehrarbeitVollzeit += m.summen.mehrarbeitVollzeit
    uebertrag.mehrarbeitTeilzeit += m.summen.mehrarbeitTeilzeit
  }
  return uebertrag
}

/**
 * Einzelner Tag ohne Wochenkontext (Hilfsfunktion für Tests/Vorschau): alle Stunden
 * liegen im Eimer „normal", Zuschläge greifen nur über Zeitfenster/Sonn-/Feiertag
 * bzw. die Betriebszeit. Für echte Auswertungen immer `berechneMonat` verwenden.
 */
export function berechneTag(
  eingabe: TagEingabe,
  soll: number,
  konfig: EngineKonfig,
  feiertagName: string | null,
  abw: AbwesenheitInfo | null,
  vortagNaechtigung = false
): TagErgebnis {
  const ti = baueTag(eingabe, soll, konfig, feiertagName, abw, vortagNaechtigung)
  bewerteTag(ti, konfig)
  return ti.e
}
